// /leads/:leadId/activity — qualification ke saare jawab aur lead ka poora safar
// (lead aayi → har call → SMS → qualified → appointment / band).
import React from "react";
import { useParams } from "react-router-dom";
import styles from "./PounceLeadScreens.module.css";
import { Icon, LeadHero, ScreenShell } from "./LeadScreenParts";
import {
  appointmentLabel,
  attemptLabel,
  dialedAttempts,
  firstContactSeconds,
  formatDuration,
  formatFull,
  formatWhen,
  isHeld,
  qualificationRows,
  sourceLabel,
  statusOf,
  useLeadDetail,
} from "../pounceLeadShared";

const CLOSED_STATUSES = ["no_answer", "not_interested", "bad_number", "opted_out", "blocked", "failed"];

const buildTimeline = (lead) => {
  const events = [];
  // Har event ki pehli line uska waqt — business ke timezone me.
  const add = (at, title, detail) =>
    at &&
    events.push({
      at: new Date(at).getTime(),
      title,
      meta: [formatFull(at, lead.displayTimezone), detail],
    });

  const source = sourceLabel(lead.source);
  add(lead.createdAt, "Lead received", source && `From ${source}`);

  const secs = firstContactSeconds(lead);
  [...dialedAttempts(lead)].reverse().forEach((a, i) => {
    const title =
      i === 0 ? (secs !== null ? `First contact in ${secs.toFixed(1)} sec` : "First call") : `Call attempt ${a.attemptNo}`;
    const result = [attemptLabel(a, { note: true }), formatDuration(a.durationSec)].filter(Boolean).join(" · ");
    add(a.startedAt || a.createdAt, title, result);
  });

  add(lead.smsSentAt, "Follow-up text sent");

  if (["qualified_no_book", "booked"].includes(lead.status)) {
    const answered = (lead.attempts || []).find((a) => a.id === lead.focusAttemptId) || {};
    add(answered.endedAt || lead.updatedAt, "Qualified by Outbound Caller", "Met qualification criteria");
  }

  if (lead.appointment) {
    const appt = appointmentLabel(lead.appointment);
    add(
      lead.appointment.createdAt,
      "Appointment booked",
      [lead.appointment.title, appt.date, appt.time].filter(Boolean).join(" · ")
    );
  }

  if (CLOSED_STATUSES.includes(lead.status)) {
    add(lead.updatedAt, statusOf(lead).label, lead.closedReason);
  }

  return events.sort((a, b) => a.at - b.at);
};

// Neeche ka note — Pounce aage kya karega.
const nextStepText = (lead) => {
  if (isHeld(lead)) {
    return "Outbound Caller is off, so this lead is on hold. Turn Outbound Caller on to choose whether to call it.";
  }
  switch (lead.status) {
    case "new":
      return "Outbound Caller will call this lead within seconds.";
    case "calling":
      return lead.nextAttemptAt
        ? `Outbound Caller will call again ${formatWhen(lead.nextAttemptAt, lead.displayTimezone)}.`
        : "Outbound Caller is on a call with this lead right now.";
    case "booked":
      return "The appointment is booked, so Outbound Caller has stopped calling this lead.";
    case "qualified_no_book":
      return "This lead qualified but didn't book. Outbound Caller won't call again — follow up with them yourself.";
    default:
      return lead.closedReason ? `${lead.closedReason}. Outbound Caller won't call this lead again.` : "Outbound Caller won't call this lead again.";
  }
};

const PounceLeadActivity = () => {
  const { leadId } = useParams();
  const { lead, error, reload } = useLeadDetail(leadId);

  return (
    <ScreenShell title="Qualification & Activity" lead={lead} error={error} reload={reload}>
      {(lead) => {
        const rows = qualificationRows(lead.analysis);
        const summary = lead.analysis?.qualification_summary;
        const timeline = buildTimeline(lead);

        return (
          <>
            <LeadHero lead={lead} />

            <section className={styles.card} aria-label="Qualification answers">
              <div className={styles.cardHead}>
                {Icon.file}
                <h2 className={styles.cardTitle}>Qualification Answers</h2>
              </div>
              {rows.length || summary ? (
                <>
                  {rows.map((r) => (
                    <div key={r.label} className={styles.kv}>
                      <span className={styles.kvLabel}>{r.label}</span>
                      <span className={styles.kvValue}>{r.value}</span>
                    </div>
                  ))}
                  {summary && <p className={styles.summaryText}>{summary}</p>}
                </>
              ) : (
                <p className={styles.muted}>No qualification answers yet — they appear after Outbound Caller talks to the lead.</p>
              )}
            </section>

            <section className={styles.card} aria-label="Activity timeline">
              <div className={styles.cardHead}>
                {Icon.clock}
                <h2 className={styles.cardTitle}>Activity Timeline</h2>
              </div>
              <ol className={styles.timeline}>
                {timeline.map((e, i) => (
                  <li key={i} className={styles.event}>
                    <p className={styles.eventTitle}>{e.title}</p>
                    {e.meta.filter(Boolean).map((m, j) => (
                      <p key={j} className={styles.eventMeta}>
                        {m}
                      </p>
                    ))}
                  </li>
                ))}
              </ol>
            </section>

            <p className={styles.info}>
              {Icon.info}
              <span>{nextStepText(lead)}</span>
            </p>
          </>
        );
      }}
    </ScreenShell>
  );
};

export default PounceLeadActivity;
